/* Copyright (c) 2017 Backpack Health LLC */

/* eslint-disable quote-props */
/* global MIXPANEL_TOKEN, IS_ANDROID_BUILD */

import mixpanel from 'mixpanel-browser';

import { authToken } from './LocalStorage';
import { ShareCard } from '../models/ShareCardModel';
import AppUtils from './AppUtils';
import { appManifest } from './appManifest';

const TOKEN = MIXPANEL_TOKEN;

const UserMetrics = {

  init() {
    if (!TOKEN) { return; }
    mixpanel.init(TOKEN);
    this.registerAppLevelProperties({
      Client: IS_ANDROID_BUILD ? 'Android' : 'Web',
      'Client Version': appManifest.app_version,
    });
    this.registerPageLevelProperties({
      Channel: 'Backpack App',
    });
  },

  registerPageLevelProperties(superProperties) {
// superProperties will be included with every subsequent request
// these would be doing the same thing as doing mixpanel.track(eventName, superProperties) later
    if (!TOKEN) { return; }
    mixpanel.register(superProperties);
  },

  registerAppLevelProperties(superProperties) {
// these superProperties will only be set, if they are not already set.
// So this could potentially be called on every page and would only set it if necessary
    if (!TOKEN) { return; }
    mixpanel.register(superProperties);
  },

  trackEvent(eventName, eventData?) {
    if (!TOKEN) { return; }
    mixpanel.track(eventName, eventData);
  },

  identifyUser(cuid) {
    if (!TOKEN) { return; }
    mixpanel.identify(cuid);
  },

  anonymousUser(cuid) {
    // call this when they sign up
    if (!TOKEN) { return; }
    mixpanel.alias(cuid);
  },

  setProfile(profileProperties) {
    if (!TOKEN) { return; }
    mixpanel.people.set(profileProperties);
  },

  incrementProfile(cuid, propertyName, amount) {
    if (!TOKEN) { return; }
    mixpanel.people.increment(propertyName, amount);
  },

  reset() {
    if (!TOKEN) { return; }
    if (mixpanel) {
      mixpanel.reset();
    }
  },

};

export const TrackEvent = {

  signup: {
    enteredEmailAddress() {
      UserMetrics.trackEvent('Entered Email Address');
    },

    acceptedPrivacy() {
      UserMetrics.trackEvent('Accepted Privacy Policy');
    },

    acceptedTermsAndPrivacy() {
      UserMetrics.trackEvent('Accepted Terms & Privacy');
    },

    viewedTermsAtSignup() {
      UserMetrics.trackEvent('Viewed Terms at Signup');
    },

    verifiedEmail() {
      UserMetrics.trackEvent('Verified Email');
    },

    setPassword() {
      UserMetrics.trackEvent('Set Password');
    },

    providedPaymentMethod(subscriptionPlanId) {
      UserMetrics.trackEvent('Provided Payment Method', {
        'Subscription Plan ID': subscriptionPlanId,
      });
    },
  },

  user: {
    setCommunicationOptions() {
      UserMetrics.trackEvent('Set Communication Options');
    },
  },

  backpackProfile: {
    viewedBackpack() {
      UserMetrics.trackEvent('Viewed Backpack');
    },
    updateModalViewed() {
      UserMetrics.trackEvent('App Update Modal - Viewed');
    },
    updateModalUpdateButton() {
      UserMetrics.trackEvent('App Update Modal - Update button');
    },
    updateModalDismissButton() {
      UserMetrics.trackEvent('App Update Modal - Dismiss button');
    },
  },

  groups: {

    searchStarted() {
      UserMetrics.trackEvent('Started Group Search');
    },

    searchCanceled(beforeSearch) {
      UserMetrics.trackEvent(beforeSearch ? 'Canceled Group Search Before Searching' : 'Canceled Group Search After Searching');
    },

    searchConducted(searchString, resultCount) {
      UserMetrics.trackEvent('Conducted Group Search', {
        'Search String': searchString,
        'Result Count': resultCount,
      });
    },

    selected(groupName) {
      UserMetrics.trackEvent('Selected Group', {
        'Group Name': groupName,
      });
    },

    eligibilityConfirmed(groupName) {
      UserMetrics.trackEvent('Confirmed Eligibility', {
        'Group Name': groupName,
      });
    },

    consentConfirmed(groupName) {
      UserMetrics.trackEvent('Confirmed Consent', {
        'Group Name': groupName,
      });
    },

    externalJoinConfirmed(groupName) {
      UserMetrics.trackEvent('Confirmed Join Group (External)', {
        'Group Name': groupName,
      });
    },

    groupInfoViewed(groupName) {
      UserMetrics.trackEvent('Group Info - Viewed', {
        'Group Name': groupName,
      });
    },

    groupCallToActionButton(groupName) {
      UserMetrics.trackEvent('Group Call-to-Action Button', {
        'Group Name': groupName,
      });
    },

  },

  shareCards: {

    shareCardQuicklinkSelectedShareCard(shareCardTemplateUid) {
      UserMetrics.trackEvent('Selected Share Cards View', {
        'Location': 'Health Info View',
        'Share Card Template': shareCardTemplateUid,
      });
    },
    shareCardQuicklinkSelectedMore() {
      UserMetrics.trackEvent('Selected Share Cards View', {
        'Location': 'Health Info View',
        'Share Card Template': null,
      });
    },
    shareCardSelectedSideNav() {
      UserMetrics.trackEvent('Selected Share Cards View', {
        'Location': 'Side Nav',
        'Share Card Template': null,
      });
    },
    shareCardQuicklinkExpanded() {
      UserMetrics.trackEvent('Expanded Share Card Quick Link');
    },

    sharePdf(fromLanguage, toLanguage, shareCardTemplateUid, isDependent, shareCardUrlEnabled, shareCardUrlIncluded) {
      UserMetrics.trackEvent('Shared PDF Share Card', {
        'From Language': fromLanguage,
        'To Language': toLanguage,
        'Share Card Template': shareCardTemplateUid,
        'Profile Type': isDependent ? 'Dependent' : 'User',
        'Share Card URL State': shareCardUrlEnabled ? 'Enabled' : 'Disabled',
        'Share Card URL Included': shareCardUrlIncluded ? 'Yes' : 'No',
      });
    },

    regeneratedAccessCode(shareCardTemplateUid, isDependent) {
      UserMetrics.trackEvent('Regenerated Access Code', {
        'Share Card Template': shareCardTemplateUid,
        'Profile Type': isDependent ? 'Dependent' : 'User',
      });
    },

    sharedUrl(shareCardTemplateUid, isDependent) {
      UserMetrics.trackEvent('Shared Share Card URL', {
        'Share Card Template': shareCardTemplateUid,
        'Profile Type': isDependent ? 'Dependent' : 'User',
      });
    },

    viewedShareCard() {
      UserMetrics.trackEvent('Viewed Share Cards');
    },

    alteredShareCard(shareCardTemplateUid, originalData: ShareCard, newData: ShareCard) {
      const alteredElements = [];

      if (AppUtils.deepCompare(originalData, newData, ['url_accessible', 'access_code']).length > 0) {
        alteredElements.push(ShareCardElementEnum.backpackUrl);
      }
      if (AppUtils.deepCompare(originalData, newData, ['share_card_jewelry']).length > 0) {
        alteredElements.push(ShareCardElementEnum.medicalId);
      }
      if (AppUtils.deepCompare(originalData, newData, ['include_name', 'include_date_of_birth', 'include_gender', 'include_birth_sex', 'include_blood_type', 'include_race', 'include_photo', 'include_residence']).length > 0) {
        alteredElements.push(ShareCardElementEnum.profileInformation);
      }
      if (AppUtils.deepCompare(originalData, newData, ['included_group_content']).length > 0) {
        alteredElements.push(ShareCardElementEnum.groupContent);
      }
      if (AppUtils.deepCompare(originalData, newData, ['included_health_entry_values'], (a, b) => (a.health_entry_id > b.health_entry_id ? 1 : -1)).length > 0) {
        alteredElements.push(ShareCardElementEnum.healthEntry);
      }
      if (AppUtils.deepCompare(originalData, newData, ['included_notes'], (a, b) => (a > b ? 1 : -1)).length > 0) {
        alteredElements.push(ShareCardElementEnum.note);
      }
      if (AppUtils.deepCompare(originalData, newData, ['included_files'], (a, b) => (a > b ? 1 : -1)).length > 0) {
        alteredElements.push(ShareCardElementEnum.file);
      }
      if (AppUtils.deepCompare(originalData, newData, ['comment']).length > 0) {
        alteredElements.push(ShareCardElementEnum.additionalNote);
      }

      if (alteredElements.length > 0) {
        UserMetrics.trackEvent('Altered Share Card', {
          'Share Card Template': shareCardTemplateUid,
          Element: alteredElements.sort().join(', '),
        });
      }
    },

    createdShareCard(shareCardTemplateUid) {
      UserMetrics.trackEvent('Created Share Card', {
        'Share Card Template': shareCardTemplateUid,
      });
    },

    registerStickyJ(shareCardTemplateUid) {
      UserMetrics.trackEvent('Share Card - Register StickyJ', {
        'Share Card Template': shareCardTemplateUid,
      });
    },

    openShareCardWallpaperFromEditView(shareCardTemplateUid) {
      UserMetrics.trackEvent('Share Card - Emergency Wallpaper', {
        'Share Card Template': shareCardTemplateUid,
        Location: 'Share Card > Edit"',
      });
    },

    openShareCardWallpaperFromSummaryView(shareCardTemplateUid) {
      UserMetrics.trackEvent('Share Card - Emergency Wallpaper', {
        'Share Card Template': shareCardTemplateUid,
        Location: 'Share Card > View',
      });
    },

  },

  medli: {
    cancelSearchBeforeSearching(medliCategory) {
      UserMetrics.trackEvent('Canceled Medli Search Before Searching', {
        'Medical List Category': medliCategory,
      });
    },
  },

  groupJoinInstructions: {
    joinGroup(groupName) {
      UserMetrics.trackEvent('Selected Join Group (External)', {
        'Group Name': groupName,
      });
    },
  },

  notUsedYet: {
    logIn(cuid, trusted) {
      UserMetrics.trackEvent('Log In', {
        cuid,
        trusted,
      });
    },

    logOut() {
      const tokenData = authToken.decode();
      UserMetrics.trackEvent('Log Out', {
        'trusted': tokenData.duration !== 'short',
      });
    },
    accountCreation() {
      UserMetrics.trackEvent('Account Creation');
    },

    mfaEnabled(deliveryMethod) {
      UserMetrics.trackEvent('2FA Enabled', {
        'Primary Delivery Method': deliveryMethod,
      });
    },

    mfaDisabled() {
      UserMetrics.trackEvent('2FA Disabled');
    },
  },

  integrationEvent: {
    medfusionEvent(eventName) {
      UserMetrics.trackEvent(eventName, {
        Channel: 'Medfusion',
      });
    },
  },

};

export const UserMetricsProfile = {

  init(cuid, creationDate) {
    if (!TOKEN) { return; }

    const userProps = {
      'UserID': cuid,
      'Account Creation Date': creationDate,
    };

    UserMetrics.registerAppLevelProperties(userProps);
    UserMetrics.setProfile(userProps);
    UserMetrics.identifyUser(cuid);
  },

};

export enum ShareCardElementEnum {
  backpackUrl = 'Backpack URL',
  medicalId = 'Medical ID',
  profileInformation = 'Profile Information',
  groupContent = 'Group Content',
  healthEntry = 'Health Entry / Health Entry Value',
  healthEntryValue = 'Health Entry / Health Entry Value',
  note = 'Note',
  file = 'File',
  additionalNote = 'Additional Note',
}

export default UserMetrics;
